import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { GetTeamDocument } from "#app/lib/data";
import type { Color, IconName } from "@salestrak/ui";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import type { Omittable } from "@salestrak/ui";

/**
 * `Team.icon` is nullable and its parts are free-form strings, but the icon
 * picker needs concrete values both to render and to send back on change. The
 * kind fallback is resolved once here so both sides of that round trip agree.
 */
export type TeamGlyph = { icon: IconName; color: Color };

type UseTeamDetailOptions = Omittable<{
  /** Additional skip condition (query already skips when teamId is falsy) */
  skip?: boolean;
  /** Hover previews pass "cache-first" so pointer travel doesn't spam network. */
  fetchPolicy?: "cache-and-network" | "cache-first";
}>;

export function useTeamDetail(teamId: string | undefined, options?: UseTeamDetailOptions) {
  const queryVariables = useMemo(() => ({ id: teamId! }), [teamId]);

  const { data, loading, error } = useQuery(GetTeamDocument, {
    variables: queryVariables,
    fetchPolicy: options?.fetchPolicy ?? "cache-and-network",
    skip: !teamId || (options?.skip ?? false),
  });

  const team = data?.team ?? null;

  const glyph = useMemo<TeamGlyph>(
    () => ({
      icon: (team?.icon?.icon as IconName) ?? ENTITY_KIND_ICON.team,
      color: (team?.icon?.color as Color) ?? "dark-grey",
    }),
    [team],
  );

  return { team, glyph, loading, error };
}
